import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Navbar, Nav, Container, NavDropdown, Badge } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGame } from '../../contexts/GameContext';
import { gameService } from '../../services/gameService';
import { HostInfo } from '../../types';

function Navigation() {
  const { user, isAuthenticated, logout } = useAuth();
  const { isInGame, gameSession } = useGame();
  const location = useLocation();
  const navigate = useNavigate();
  const [hostInfo, setHostInfo] = useState<HostInfo | null>(null);
  const [loadingTokens, setLoadingTokens] = useState<boolean>(false);
  const isTeaserPage = location.pathname === '/';

  // Function to get badge color based on UserType - memoized to prevent recreation on re-renders
  const getUserTypeBadgeColor = useMemo(() => {
    return (userType: string) => {
      switch (userType) {
        case 'Admin': return 'danger';
        case 'Member': return 'warning';
        case 'Player': return 'secondary';
        case 'Guest': return 'light';
        default: return 'secondary';
      }
    };
  }, []);

  // Memoized fetch host info function to prevent recreation on every render
  const fetchHostInfo = useCallback(async () => {
    if (isAuthenticated && user && (user.userType === 'Member' || user.userType === 'Player')) {
      setLoadingTokens(true);
      try {
        const response = await gameService.getHostInfo();
        if (response.success && response.data) {
          setHostInfo(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch host info:', error);
      } finally {
        setLoadingTokens(false);
      }
    }
  }, [isAuthenticated, user]);

  // Fetch host info when component mounts or dependencies change
  useEffect(() => {
    fetchHostInfo();
  }, [fetchHostInfo]);

  // Memoized logout handler to prevent recreation on every render
  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
  }, [logout, navigate]);

  return (
    <Navbar bg="white" expand="lg" className="border-bottom shadow-sm">
      <Container>
        <Navbar.Brand as={Link} to="/" className="fw-bold text-primary fs-3">
          <i className="fas fa-gamepad me-2"></i>
          TriviYolo
        </Navbar.Brand>

        {isTeaserPage && (
          <div className="ms-auto d-flex align-items-center">
            <Badge bg="warning" text="dark" className="px-3 py-2">
              <i className="fas fa-lock me-1"></i>
              Coming Soon
            </Badge>
          </div>
        )}

        {!isTeaserPage && <Navbar.Toggle aria-controls="basic-navbar-nav" />}
        
        <Navbar.Collapse id="basic-navbar-nav" className={isTeaserPage ? 'd-none' : undefined}>
          <Nav className="me-auto">
            {!isTeaserPage && (
              <>
                <Nav.Link as={Link} to="/">
                  <i className="fas fa-home me-1"></i>
                  Home
                </Nav.Link>
                {isAuthenticated && user && (user.userType === 'Member' || user.userType === 'Player') && (
                  <Nav.Link as={Link} to="/gamespace">
                    <i className="fas fa-dice me-1"></i>
                    My GameSpace
                  </Nav.Link>
                )}
              </>
            )}
          </Nav>

          <Nav>
            {/* Game Status */}
            {!isTeaserPage && isInGame && gameSession && (
              <Nav.Item className="d-flex align-items-center me-3">
                <Badge bg="success" className="px-3 py-2">
                  <i className="fas fa-gamepad me-1"></i>
                  In Game: {gameSession.gameCode}
                </Badge>
              </Nav.Item>
            )}
            
            {/* Token Balance for Member/Player */}
            {!isTeaserPage && isAuthenticated && user && (user.userType === 'Member' || user.userType === 'Player') && (
              <Nav.Item className="d-flex align-items-center me-3">
                <Badge bg="info" className="px-3 py-2">
                  <i className="fas fa-coins me-1"></i>
                  {loadingTokens ? (
                    'Loading tokens...'
                  ) : (
                    `Tokens: ${hostInfo?.tokenBalance || 0}`
                  )}
                </Badge>
              </Nav.Item>
            )}

            {/* User Menu */}
            {!isTeaserPage && isAuthenticated && user ? (
              <NavDropdown
                title={
                  <span className="d-flex align-items-center">
                    {user.profileImageUrl ? (
                      <img 
                        src={user.profileImageUrl} 
                        alt={user.displayName} 
                        className="rounded-circle me-2" 
                        style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                      />
                    ) : (
                      <i className="fas fa-user-circle me-2"></i>
                    )}
                    {user.displayName}
                    {user.userType && (
                      <Badge bg={getUserTypeBadgeColor(user.userType)} 
                             text={getUserTypeBadgeColor(user.userType) === 'light' || getUserTypeBadgeColor(user.userType) === 'warning' ? 'dark' : 'white'} 
                             className="ms-1 small">
                        {user.userType}
                      </Badge>
                    )}
                  </span>
                }
                id="user-nav-dropdown"
                align="end"
              >
                {!user.isGuest && (
                  <>
                    <NavDropdown.Item as={Link} to="/profile">
                      <i className="fas fa-user me-2"></i>
                      Profile
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/gamespace">
                      <i className="fas fa-gamepad me-2"></i>
                      My Games
                    </NavDropdown.Item>
                    {user.userType === 'Member' && (
                      <NavDropdown.Item as={Link} to="/pro">
                        <i className="fas fa-crown me-2"></i>
                        Member Management
                      </NavDropdown.Item>
                    )}
                    {(user.userType === 'Player' || user.userType === 'Member') && (
                      <NavDropdown.Item as={Link} to="/tokens">
                        <i className="fas fa-coins me-2"></i>
                        Buy Tokens
                      </NavDropdown.Item>
                    )}
                    <NavDropdown.Divider />
                  </>
                )}
                
                {user.isGuest && (
                  <>
                    <NavDropdown.Item as={Link} to="/login">
                      <i className="fas fa-user-plus me-2"></i>
                      Create Account
                    </NavDropdown.Item>
                    <NavDropdown.Divider />
                  </>
                )}
                
                <NavDropdown.Item onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt me-2"></i>
                  Sign Out
                </NavDropdown.Item>
              </NavDropdown>
            ) : !isTeaserPage ? (
              <Nav.Link as={Link} to="/login" className="btn btn-outline-primary">
                <i className="fas fa-sign-in-alt me-1"></i>
                Sign In
              </Nav.Link>
            ) : null}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

// Wrap the component with memo to prevent unnecessary re-renders when props haven't changed
export default memo(Navigation);
